import { useFieldArray, type UseFormReturn } from "react-hook-form";
import { useEffect, useState } from "react";

import Input from "./input.core";
import Button from "./button.core";
import {
  type IzCampaignFormBrand,
  MilestoneRewardType,
} from "../../../z/brand/campaign-form.brand.z";
import DeleteIcon from "../../../assets/svgs/common/delete.svg";
import { PlusIcon } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "./sheet.core";
import { ThreeDWrapper } from "./3d-wrapper.core";
import DocumentSmall from "../../../assets/svgs/campaigns/doc.svg";
import UploadIcon from "../../../assets/svgs/common/upload.svg";

type MileStoneWhiteProps = {
  form: UseFormReturn<IzCampaignFormBrand>;
  label?: string;
  required?: boolean;
  payoutType: MilestoneRewardType;
};

const MileStoneWhite = ({
  form,
  label = "Set milestones",
  payoutType,
}: MileStoneWhiteProps) => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "milestones",
  });

  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const [product, setProduct] = useState<{
    file: File | null;
    name: string;
    editIndex?: number;
    isExistingProduct?: boolean;
  }>({
    file: null,
    name: "",
    editIndex: undefined,
    isExistingProduct: false,
  });

  useEffect(() => {
    if (fields.length === 0) {
      append({
        count: 0,
        payout: null,
        product: null,
        productFile: null,
      });
    }
  }, [fields.length, append]);

  const closeSheet = () => {
    document.getElementById("closeSheet")?.click();
    setTimeout(() => {
      setIsSheetOpen(false);
      setProduct({
        file: null,
        name: "",
        editIndex: undefined,
        isExistingProduct: false,
      });
    }, 100);
  };

  const handleProduct = () => {
    if ((!product.file && !product.isExistingProduct) || !product.name.trim())
      return;

    const index = product.editIndex ?? 0;

    if (product.isExistingProduct) {
      // Update existing product name only
      const existingProduct = form.watch(`milestones.${index}.product`);
      if (existingProduct) {
        form.setValue(
          `milestones.${index}.product`,
          {
            ...existingProduct,
            name: product.name,
          },
          { shouldDirty: true }
        );
      }
    } else if (product.file) {
      // Add/update new product file
      form.setValue(
        `milestones.${index}.productFile`,
        {
          file: product.file,
          name: product.name,
        },
        { shouldDirty: true }
      );
    }

    closeSheet();
  };

  const handleEditProduct = (index: number) => {
    // Check if there's a productFile (new upload) first
    const productFile = form.watch(`milestones.${index}.productFile`);
    if (productFile) {
      setProduct({
        file: productFile.file,
        name: productFile.name,
        editIndex: index,
        isExistingProduct: false,
      });
      setIsSheetOpen(true);
      return;
    }

    // Check if there's an existing product from server
    const existingProduct = form.watch(`milestones.${index}.product`);
    if (existingProduct) {
      setProduct({
        file: null,
        name: existingProduct.name,
        editIndex: index,
        isExistingProduct: true,
      });
      setIsSheetOpen(true);
    }
  };

  const handleDeleteProduct = (index: number) => {
    // Check if it's a new upload or existing product
    const productFile = form.watch(`milestones.${index}.productFile`);
    const existingProduct = form.watch(`milestones.${index}.product`);

    if (productFile) {
      form.setValue(`milestones.${index}.productFile`, null, {
        shouldDirty: true,
      });
    }

    if (existingProduct) {
      form.setValue(`milestones.${index}.product`, null, {
        shouldDirty: true,
      });
    }
  };

  // Check if milestone has product (either new or existing)
  const hasProduct = (index: number) => {
    const productFile = form.watch(`milestones.${index}.productFile`);
    const existingProduct = form.watch(`milestones.${index}.product`);
    return !!(productFile || existingProduct);
  };

  // Get product data for display (prioritize new upload over existing)
  const getProductData = (index: number) => {
    const productFile = form.watch(`milestones.${index}.productFile`);
    if (productFile) {
      return {
        name: productFile.name,
        imageUrl: URL.createObjectURL(productFile.file),
        isNew: true,
      };
    }

    const existingProduct = form.watch(`milestones.${index}.product`);
    if (existingProduct) {
      return {
        name: existingProduct.name,
        imageUrl: existingProduct.image.url,
        isNew: false,
      };
    }

    return null;
  };

  if (payoutType === MilestoneRewardType.CASH) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <h3 className="font-medium text-base">{label}</h3>

        <div className="flex flex-col gap-4">
          {fields.map((item, index) => {
            return (
              <div
                key={item.id}
                className="gap-4 grid grid-cols-[1fr_1fr_auto_auto]"
              >
                {/* Count Input */}
                <div className="relative">
                  <Input
                    placeholder={index === 0 ? "On Post Approval" : "10,000"}
                    value={form.watch(`milestones.${index}.count`) || ""}
                    onChange={(e) => {
                      form.setValue(
                        `milestones.${index}.count`,
                        Number(e.target.value) as number,
                        { shouldDirty: true }
                      );
                    }}
                    type="number"
                    className="pr-16"
                    fullWidth
                  />
                  <span className="top-1/2 right-4 absolute text-gray-400 -translate-y-1/2">
                    views
                  </span>
                </div>

                {/* Payout Input */}
                <div className="relative">
                  <span className="top-1/2 left-4 z-10 absolute font-medium -translate-y-1/2">
                    ₹
                  </span>
                  <Input
                    placeholder="1,000"
                    value={form.watch(`milestones.${index}.payout`) || ""}
                    onChange={(e) => {
                      form.setValue(
                        `milestones.${index}.payout`,
                        Number(e.target.value) as number,
                        { shouldDirty: true }
                      );
                    }}
                    type="number"
                    className="pl-8"
                    fullWidth
                  />
                </div>

                {/* Delete Button */}
                <Button
                  type="button"
                  variant="danger"
                  disabled={index === 0 && fields.length === 1}
                  onClick={() => remove(index)}
                  className="flex justify-center items-center p-0 rounded-xl w-20 h-full text-white transition-colors"
                >
                  <DeleteIcon />
                </Button>
              </div>
            );
          })}
        </div>

        <div className="w-64">
          <Button
            type="button"
            onClick={() =>
              append({
                count: 0,
                payout: 0,
                product: null,
                productFile: null,
              })
            }
            variant="primary"
            className="px-6 py-4 rounded-xl w-full text-base"
          >
            Add more milestones +
          </Button>
        </div>
      </div>
    );
  }

  if (payoutType === MilestoneRewardType.PRODUCT) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <h3 className="font-medium text-base">{label}</h3>

        <div className="flex flex-col gap-4">
          {/* Other Milestones */}
          {fields.map((item, index) => {
            return (
              <div
                key={item.id}
                className="gap-4 grid grid-cols-[1fr_1fr_auto]"
              >
                {/* Count Input */}
                <div className="relative">
                  <Input
                    placeholder={index === 0 ? "On Post Approval" : "10,000"}
                    value={form.watch(`milestones.${index}.count`) || ""}
                    onChange={(e) => {
                      form.setValue(
                        `milestones.${index}.count`,
                        Number(e.target.value) as number,
                        { shouldDirty: true }
                      );
                    }}
                    type="number"
                    className="pr-16"
                    fullWidth
                  />
                  <span className="top-1/2 right-4 absolute text-gray-400 -translate-y-1/2">
                    views
                  </span>
                </div>

                {/* Product */}
                <div className="relative w-full">
                  {!hasProduct(index) ? (
                    <Button
                      onClick={() => {
                        setProduct({
                          file: null,
                          name: "",
                          editIndex: index,
                          isExistingProduct: false,
                        });
                        setIsSheetOpen(true);
                      }}
                      type="button"
                      variant="outline"
                      className="py-6 border-dashed w-full text-base"
                    >
                      <PlusIcon className="mr-2" /> Add product
                    </Button>
                  ) : (
                    <ProductPreview
                      productData={getProductData(index)}
                      onEdit={() => handleEditProduct(index)}
                      onDelete={() => handleDeleteProduct(index)}
                    />
                  )}
                </div>

                {/* Delete Button */}
                <Button
                  type="button"
                  variant="danger"
                  disabled={index === 0 && fields.length === 1}
                  onClick={() => remove(index)}
                  className="flex justify-center items-center p-0 rounded-xl w-20 h-full text-white transition-colors"
                >
                  <DeleteIcon />
                </Button>
              </div>
            );
          })}
        </div>

        <div className="w-64">
          <Button
            type="button"
            onClick={() =>
              append({
                count: 0,
                payout: 0,
                product: null,
                productFile: null,
              })
            }
            variant="primary"
            className="px-6 py-4 rounded-xl w-full text-base"
          >
            Add more milestones +
          </Button>
        </div>

        <Sheet
          open={isSheetOpen}
          onOpenChange={(open) => {
            if (!open && isSheetOpen) {
              closeSheet();
            }
          }}
        >
          <ProductSheetContent
            product={product}
            setProduct={setProduct}
            handleProduct={handleProduct}
          />
        </Sheet>
      </div>
    );
  }

  if (payoutType === MilestoneRewardType.CASH_PRODUCT) {
    return (
      <div className="flex flex-col gap-4 w-full">
        <h3 className="font-medium text-base">{label}</h3>

        <div className="flex flex-col gap-4">
          {/* Other Milestones */}
          {fields.map((item, index) => {
            return (
              <div key={item.id} className="w-full h-full">
                <div className="flex flex-col gap-2 w-full h-full">
                  <div className="items-center gap-4 grid grid-cols-2 w-full h-full">
                    {/* Count Input */}
                    <div className="relative w-full">
                      <Input
                        placeholder={
                          index === 0 ? "On Post Approval" : "10,000"
                        }
                        value={form.watch(`milestones.${index}.count`) || ""}
                        onChange={(e) => {
                          form.setValue(
                            `milestones.${index}.count`,
                            Number(e.target.value) as number,
                            { shouldDirty: true }
                          );
                        }}
                        type="number"
                        className="pr-16"
                        fullWidth
                      />
                      <span className="top-1/2 right-4 absolute text-gray-400 -translate-y-1/2">
                        views
                      </span>
                    </div>
                    <div className="flex items-center gap-4 h-full">
                      {/* Payout Input */}
                      <div className="relative w-full h-full">
                        <span className="top-1/2 left-4 z-10 absolute font-medium -translate-y-1/2">
                          ₹
                        </span>
                        <Input
                          placeholder="1,000"
                          value={form.watch(`milestones.${index}.payout`) || ""}
                          onChange={(e) => {
                            form.setValue(
                              `milestones.${index}.payout`,
                              Number(e.target.value) as number,
                              { shouldDirty: true }
                            );
                          }}
                          type="number"
                          className="pl-8"
                          fullWidth
                        />
                      </div>
                      <div className="grid h-full">
                        {/* Delete Button */}
                        <Button
                          type="button"
                          variant="danger"
                          disabled={index === 0 && fields.length === 1}
                          onClick={() => remove(index)}
                          className="justify-center items-center p-0 rounded-xl w-20 h-full text-white transition-colors"
                        >
                          <DeleteIcon />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {/* Product */}
                  <div className="gap-4 grid grid-cols-2 w-full">
                    <div className="" />
                    <div className="relative">
                      {!hasProduct(index) ? (
                        <Button
                          onClick={() => {
                            setProduct({
                              file: null,
                              name: "",
                              editIndex: index,
                              isExistingProduct: false,
                            });
                            setIsSheetOpen(true);
                          }}
                          type="button"
                          variant="outline"
                          className="py-6 border-dashed w-full text-base"
                        >
                          <PlusIcon className="mr-2" /> Add product
                        </Button>
                      ) : (
                        <ProductPreview
                          productData={getProductData(index)}
                          onEdit={() => handleEditProduct(index)}
                          onDelete={() => handleDeleteProduct(index)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="w-64">
          <Button
            type="button"
            onClick={() =>
              append({
                count: 0,
                payout: null,
                product: null,
                productFile: null,
              })
            }
            variant="primary"
            className="px-6 py-4 rounded-xl w-full text-base"
          >
            Add more milestones +
          </Button>
        </div>

        <Sheet
          open={isSheetOpen}
          onOpenChange={(open) => {
            if (!open && isSheetOpen) {
              closeSheet();
            }
          }}
        >
          <ProductSheetContent
            product={product}
            setProduct={setProduct}
            handleProduct={handleProduct}
          />
        </Sheet>
      </div>
    );
  }
};

// Product Preview Component - Updated to handle both new and existing products
const ProductPreview = ({
  productData,
  onEdit,
  onDelete,
}: {
  productData: { name: string; imageUrl: string; isNew: boolean } | null;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  if (!productData) return null;

  return (
    <div className="flex justify-between items-center gap-3 px-4 py-2 border-2 border-black rounded-xl">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        className="flex flex-1 items-center gap-3 cursor-pointer"
        onClick={onEdit}
      >
        <img
          src={productData.imageUrl}
          alt={productData.name}
          className="rounded-lg w-12 h-12 object-cover"
        />
        <p className="font-medium text-sm truncate">{productData.name}</p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="text-red-500 hover:text-red-600 transition-colors"
      >
        <DeleteIcon />
      </button>
    </div>
  );
};

const ProductSheetContent = ({
  product,
  setProduct,
  handleProduct,
}: {
  product: {
    file: File | null;
    name: string;
    editIndex?: number;
    isExistingProduct?: boolean;
  };
  setProduct: React.Dispatch<
    React.SetStateAction<{
      file: File | null;
      name: string;
      editIndex?: number;
      isExistingProduct?: boolean;
    }>
  >;
  handleProduct: () => void;
}) => {
  const isEditMode = product.editIndex !== undefined;

  return (
    <SheetContent className="bg-white p-0 border-border w-full max-w-2xl font-cabinetgrotesk">
      <SheetHeader className="mb-0 p-5 border-black border-b">
        <SheetTitle className="flex items-center gap-3">
          {isEditMode ? "Edit product" : "Add product"}
        </SheetTitle>
      </SheetHeader>

      <div className="flex flex-col justify-between gap-4 p-5 w-full h-[calc(100vh-80px)] overflow-y-auto">
        <div className="flex flex-col gap-4 text-black">
          <Input
            placeholder="Product name here..."
            label="Product Name"
            fullWidth
            value={product.name}
            onChange={(e) =>
              setProduct((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
          />

          {/* Only show file upload if not editing existing product from server */}
          {!product.isExistingProduct && (
            <div className="flex flex-col gap-4 w-full">
              {/* File Upload */}
              {!product.file ? (
                // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                <div
                  className="flex justify-center items-center gap-5 px-6 py-6 border border-black border-dashed rounded-xl text-black transition-colors cursor-pointer"
                  onClick={() => {
                    document.getElementById("product-file-input")?.click();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      document.getElementById("product-file-input")?.click();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <UploadIcon />
                  <p className="font-medium text-base">Upload product image</p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between items-center gap-4 bg-gray-50 px-4 py-3 border border-black border-dashed rounded-xl">
                    <div className="flex items-center gap-3">
                      <ThreeDWrapper
                        hover={false}
                        className="flex justify-center items-center p-0 rounded-lg w-10 h-10 text-black text-xl"
                      >
                        <DocumentSmall />
                      </ThreeDWrapper>
                      <p className="font-medium text-black text-sm truncate">
                        {product.file.name.length > 30
                          ? `${product.file.name.substring(0, 30)}...`
                          : product.file.name}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setProduct((prev) => ({
                          ...prev,
                          file: null,
                        }));
                      }}
                      className="text-red-500 hover:text-red-600 transition-colors"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </div>
              )}

              <input
                id="product-file-input"
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setProduct((prev) => ({
                      ...prev,
                      file,
                    }));
                  }
                  e.target.value = "";
                }}
              />
            </div>
          )}
        </div>

        <SheetFooter className="w-full">
          <div className="border-t w-full">
            <Button
              type="button"
              onClick={handleProduct}
              disabled={
                product.isExistingProduct
                  ? !product.name.trim()
                  : !product.file || !product.name.trim()
              }
              className="py-4 border border-black rounded-xl w-full font-semibold text-base text-black"
            >
              {isEditMode ? "Update product →" : "Add product →"}
            </Button>
          </div>
        </SheetFooter>
      </div>
    </SheetContent>
  );
};

export default MileStoneWhite;
